<script setup lang="ts">
/**
 * UI-kit component
 * Category: Fields
 * Name: NativeSelect
 */
import type { NativeSelectOption, NativeSelectProps } from '../types/interfaces'

import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { AppText } from '@shared/ui/typography'
import { IconContainer } from '@shared/ui/other'

import { useNativeSelect } from '../composables/use-native-select.hook'

defineOptions({
  name: 'NativeSelect',
})

const props = withDefaults(defineProps<NativeSelectProps>(), {
  id: undefined,
  label: '',
  value: null,
  disabled: false,
  required: false,
  mode: 'normal',
  view: 'default',
  placeholder: 'Select option',
  placeholderEnabled: false,
  changeOnMount: false,
  icon: null,
})

const emit = defineEmits<{
  change: [NativeSelectOption]
}>()

const {
  refOptions,
  refCurrent,
  colorText,
  isPlaceholder,
  placeholderValue,
  handleChange,
} = useNativeSelect({
  props,
  onChange: (option: NativeSelectOption) => emit('change', option),
})
</script>

<template>
  <div :class="['ui-sel-native', view]">
    <AppText
      v-if="label"
      name="body2"
      :tag="id ? 'label' : 'div'"
      :for="id"
    >
      {{ label }} <span
        v-if="required"
        class="ui-sel-native__required"
      >*</span>
    </AppText>
    <div class="ui-sel-native__box">
      <div
        v-if="icon"
        class="ui-sel-native__icon"
      >
        <IconContainer>
          <FontAwesomeIcon :icon="icon" />
        </IconContainer>
      </div>
      <div
        v-if="!disabled"
        class="ui-sel-native__arrow"
      >
        <IconContainer>
          <FontAwesomeIcon :icon="faChevronDown" />
        </IconContainer>
      </div>
      <select
        :id="id"
        v-model="refCurrent"
        :class="['ui-sel-native__select', { disabled, readonly, 'with-icon': !!icon, placeholder: isPlaceholder }, colorText, mode, view]"
        :name="name"
        :disabled="disabled"
        @change="handleChange"
      >
        <option
          v-if="placeholderEnabled"
          :value="placeholderValue"
          disabled
          selected
        >
          {{ placeholder }}
        </option>
        <option
          v-for="(option, index) in refOptions"
          :key="index"
          :value="option.value"
          :disabled="option.disabled"
          :hidden="option.hidden"
          :selected="option.selected"
        >
          {{ option.text }}
        </option>
      </select>
    </div>
  </div>
</template>

<style lang="scss" scoped src="../styles/_native-select.scss" />
